import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../authContext/AuthProvider";
import "../styles/Auth.css";
import { FaHeadset } from "react-icons/fa";

const SignupScreen = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  // ------------------------------
  // FORM VALIDATION (Frontend)
  // ------------------------------

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    setGeneralError("");

    return Object.keys(newErrors).length === 0;
  };

  // ------------------------------
  // INPUT HANDLER
  // ------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear specific error
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Clear general error
    if (generalError) setGeneralError("");
  };

  // ------------------------------
  // BACKEND ERROR MAPPING (Improved)
  // ------------------------------

  const mapBackendErrors = (backendErrors) => {
    const fieldMap = {
      password_confirm: "confirmPassword",
      password_confirmation: "confirmPassword",
      non_field_errors: "general",
      detail: "general",
      message: "general"
    };

    let mapped = {};
    let general = "";

    Object.entries(backendErrors).forEach(([field, msg]) => {
      const text =
        Array.isArray(msg) ? msg.join(" ") : typeof msg === "string" ? msg : "";

      const mappedField = fieldMap[field] || field;

      if (mappedField === "general") {
        general = text;
      } else {
        mapped[mappedField] = text;
      }
    });

    setErrors(mapped);
    setGeneralError(general);
  };

  // ------------------------------
  // SUBMIT HANDLER
  // ------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const result = await signup(
      formData.name,
      formData.email,
      formData.password,
      formData.confirmPassword
    );

    setIsSubmitting(false);

    if (result.success) {
      toast.success("Account created successfully! Please login.");
      navigate("/login");
      return;
    }

    // Clear past errors
    setErrors({});
    setGeneralError("");

    // Object with field errors
    if (typeof result.error === "object" && result.error !== null) {
      mapBackendErrors(result.error);
    }
    // Simple error message string
    else if (typeof result.error === "string") {
      toast.error(result.error);
      setGeneralError(result.error);
    }
    // Fallback
    else {
      const msg = "Something went wrong. Please try again.";
      toast.error(msg);
      setGeneralError(msg);
    }
  };

  // ------------------------------
  // RENDER
  // ------------------------------

  return (

    

    <div className="auth-container">
      <div className="auth-header">
  <div className="auth-logo-icon">
    <FaHeadset size={34} />
  </div>
  <h1>Create Account</h1>
  <p>Sign up to get started</p>
</div>

      <div className="auth-card">

        <div className="auth-header">
          <h1>Create Account</h1>
          <p>Sign up to get started</p>
        </div>

        {generalError && (
          <div className="auth-alert auth-alert-error">{generalError}</div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>

          {/* Name */}
          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className={errors.name ? "is-invalid" : ""}
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              aria-invalid={!!errors.name}
            />
            {errors.name && <div className="error-text">{errors.name}</div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={errors.email ? "is-invalid" : ""}
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              aria-invalid={!!errors.email}
            />
            {errors.email && <div className="error-text">{errors.email}</div>}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required">*</span>
            </label>

            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className={errors.password ? "is-invalid" : ""}
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {errors.password && (
              <div className="error-text">{errors.password}</div>
            )}

            <small className="helper-text">Must be at least 8 characters.</small>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required">*</span>
            </label>

            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className={errors.confirmPassword ? "is-invalid" : ""}
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {errors.confirmPassword && (
              <div className="error-text">{errors.confirmPassword}</div>
            )}
          </div>

          {/* Submit Button */}
          <button className="submit-button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating Account…" : "Create Account"}
          </button>

          <div className="auth-links">
            <p>
              Already have an account? <Link to="/login">Sign In</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupScreen;
